// src/runtime/GrowthFactory/geoPlatforms.ts
// ============================================
// GEO+DSO 诊断平台常量库
//
// 用途：为「实时搜索验证 / 产品诊断 / 品牌诊断」三个诊断功能提供统一平台元数据、
//       真实 API 代理端点、回答风格 prompt，以及品牌/产品提及解析与评分工具。
//
// 数据真实性约束：
//   · 全部走服务端代理 /{platform}-api（key 服务端持有，前端不暴露）
//   · 解析与评分基于真实 AI 回答文本，无 mock、无硬编码结果
//   · 平台集合与 BrandMonitorsHub / AiVisibilityMonitor 保持一致（已线上验证）
// ============================================

/** 诊断平台键（与服务端代理 /{platform}-api 一一对应） */
export type DiagPlatformKey =
  | 'doubao'
  | 'ernie'
  | 'qwen'
  | 'deepseek'
  | 'yuanbao'
  | 'kimi'
  | 'baidu';

/** 平台元数据 */
export interface DiagPlatformMeta {
  key: DiagPlatformKey;
  /** 平台中文名 */
  name: string;
  /** 品牌色 */
  color: string;
  /** 单字缩写 */
  short: string;
  /** 服务端代理端点 */
  endpoint: string;
}

/** 7 大 AI 平台（全部真实 API 接入；百度AI走千帆 ERNIE 联网搜索模拟百度AI搜索场景） */
export const DIAG_PLATFORMS: DiagPlatformMeta[] = [
  { key: 'doubao', name: '豆包', color: '#3B82F6', short: '豆', endpoint: '/doubao-api' },
  { key: 'ernie', name: '文心一言', color: '#EF4444', short: '文', endpoint: '/ernie-api' },
  { key: 'qwen', name: '通义千问', color: '#8B5CF6', short: '通', endpoint: '/qwen-api' },
  { key: 'yuanbao', name: '腾讯元宝', color: '#06B6D4', short: '元', endpoint: '/yuanbao-api' },
  { key: 'deepseek', name: 'DeepSeek', color: '#10B981', short: 'D', endpoint: '/deepseek-api' },
  { key: 'kimi', name: 'Kimi', color: '#F59E0B', short: 'K', endpoint: '/kimi-api' },
  { key: 'baidu', name: '百度AI', color: '#2932E1', short: '百', endpoint: '/baidu-api' },
];

/** 平台元数据快速查找 */
export const DIAG_PLATFORM_MAP: Record<DiagPlatformKey, DiagPlatformMeta> =
  DIAG_PLATFORMS.reduce((acc, p) => {
    acc[p.key] = p;
    return acc;
  }, {} as Record<DiagPlatformKey, DiagPlatformMeta>);

/** 各平台回答风格（用于让模型扮演对应助手，保持输出风格差异） */
export const DIAG_STYLE_PROMPT: Record<DiagPlatformKey, string> = {
  doubao: '你是豆包，字节跳动的 AI 助手，回答口语化、简洁、亲切实用。请客观、如实回答。',
  ernie: '你是文心一言，百度的 AI 助手，回答稳重、条理清晰。请客观、如实回答。',
  qwen: '你是通义千问，阿里巴巴的 AI 助手，回答专业、结构完整。请客观、如实回答。',
  yuanbao: '你是腾讯元宝，腾讯的 AI 助手，回答清晰、务实。请客观、如实回答。',
  deepseek: '你是 DeepSeek，回答简洁、直接、实事求是。请客观、如实回答。',
  kimi: '你是 Kimi，回答细致、条理清晰、善于总结。请客观、如实回答。',
  baidu: '你是百度AI搜索助手，回答简洁、准确、注重信息来源和权威性。请客观、如实回答。',
};

/** DeepSeek 兜底端点（当某平台代理失败时用于模拟该平台回答） */
export const DIAG_FALLBACK_ENDPOINT = '/deepseek-api/v1/chat/completions';

// ============================================
// 提及解析工具
// ============================================

/**
 * 解析回答文本中某品牌/产品实体的提及情况
 *
 * @param text  AI 原始回答
 * @param terms 实体词集合（如 ['小舍科技', 'xeaos']），命中任一即视为被提及
 */
export function parseEntityMention(
  text: string,
  terms: string[]
): {
  /** 是否被提及 */
  mentioned: boolean;
  /** 品牌/产品词在回答中出现的位置（字符索引，-1 表示未提及） */
  position: number;
  /** 是否在回答开头（前 120 字符内）出现，视为「首位/首推」 */
  first: boolean;
  /** 是否在回答前 400 字符内出现，视为「前 3 推荐位」 */
  top3: boolean;
  /** 命中词 */
  hitTerm: string;
  /** 围绕命中词截取的引用片段 */
  snippet: string;
  /** 正文中实体词出现次数 */
  count: number;
} {
  const lower = (text || '').toLowerCase();
  const normalized = terms
    .map((t) => t.trim().toLowerCase())
    .filter((t) => t.length > 0);

  let hitTerm = '';
  let position = -1;
  let count = 0;

  for (const term of normalized) {
    let idx = lower.indexOf(term);
    if (idx >= 0) {
      if (position === -1 || idx < position) position = idx;
      hitTerm = term;
      // 统计该词出现次数（简单正则计数，转义特殊字符）
      try {
        const re = new RegExp(term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
        count += (lower.match(re) || []).length;
      } catch {
        count += 1;
      }
    }
  }

  const mentioned = position >= 0;
  const first = mentioned && position < 120;
  const top3 = mentioned && position < 400;
  let snippet = '';
  if (mentioned) {
    const start = Math.max(0, position - 30);
    snippet = (text || '').slice(start, start + 160).replace(/\s+/g, ' ').trim();
  }

  return { mentioned, position, first, top3, hitTerm, snippet, count };
}

// ============================================
// 产品卡结构化特征检测
// ============================================

/**
 * 检测回答中「产品卡」结构化特征（标题 / 功能列表 / 价格 / 适用场景 / 优劣势）
 *
 * @param text  AI 原始回答
 * @param product 产品名
 * @returns 命中的特征数组 + 得分（0-5）
 */
export function detectProductCard(
  text: string,
  product: string
): { hits: string[]; score: number } {
  const t = (text || '').toLowerCase();
  const p = product.trim().toLowerCase();
  const hits: string[] = [];

  // ① 产品名标题出现（# 或加粗或开头）
  if (p && (t.includes(p) || t.includes(p.replace(/\s+/g, '')))) {
    if (/#|【|《|：|:/.test(t.slice(0, Math.max(0, t.indexOf(p) + p.length) + 12)) || /#{1,3}\s/.test(t)) {
      hits.push('产品标题');
    }
  }

  // ② 功能/特性列表（- 或数字 或 、分隔 或 关键词）
  if (/(功能|特性|特点|亮点|能力)/.test(t) && /([-•*]|\d[.、)]|，|、)/.test(t)) {
    hits.push('功能列表');
  }

  // ③ 价格信息
  if (/(价格|售价|报价|收费|多少钱|元\/|¥|￥|\d+\s*元|免费)/.test(t)) {
    hits.push('价格信息');
  }

  // ④ 适用场景/客户
  if (/(适用|适合|场景|面向|客户|行业|使用人群)/.test(t)) {
    hits.push('适用场景');
  }

  // ⑤ 优劣势/评价
  if (/(优势|劣势|优点|缺点|推荐|评价|对比|不足)/.test(t)) {
    hits.push('优劣势评价');
  }

  return { hits, score: hits.length };
}

// ============================================
// 评分工具
// ============================================

/** 根据提及率换算可见性得分（0-100） */
export function visibilityScore(mentioned: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((mentioned / total) * 100);
}

/** 品牌综合诊断得分：提及率(60%) + 首推率(40%) */
export function brandDiagnosisScore(
  mentionRate: number,
  firstRate: number
): number {
  return Math.round(mentionRate * 60 + firstRate * 40);
}

/** 产品诊断综合得分：产品卡得分(50%) + 提及率(50%) */
export function productDiagnosisScore(
  cardScore: number,
  mentionRate: number
): number {
  const cardPart = Math.min(1, cardScore / 5) * 50;
  const mentionPart = mentionRate * 50;
  return Math.round(cardPart + mentionPart);
}
